/**
 * ProcessingStateFlow Component
 * An interactive SVG state machine diagram showing all states and transitions
 */

import { useState } from 'react';
import {
    STATE_TRANSITIONS,
    getStateInfo,
    getAvailableActions,
    isProcessingState,
} from '../utils/processingState';

// Node positions in the SVG - wider spacing for readability
const NODE_POSITIONS = {
    DRAFT: { x: 70, y: 100 },
    DISCOVERED: { x: 190, y: 100 },
    CRAWLING: { x: 310, y: 100 },
    CRAWLED: { x: 430, y: 100 },
    ENRICHING: { x: 510, y: 40 },
    ENRICHED: { x: 630, y: 40 },
    PROCESSING: { x: 570, y: 100 },
    PROCESSED: { x: 710, y: 100 },
    ASSESSING: { x: 850, y: 100 },
    ASSESSED: { x: 990, y: 100 },
    ARCHIVED: { x: 990, y: 190 },
    ERROR: { x: 430, y: 190 },
};

const NODE_WIDTH = 90;
const NODE_HEIGHT = 40;

const ProcessingStateFlow = ({
    currentState,
    onNodeClick,
    showLegend = true,
}) => {
    const [hoveredState, setHoveredState] = useState(null);

    // eslint-disable-next-line no-unused-vars
    const getNodeColor = (state) => {
        const info = getStateInfo(state);
        if (state === currentState) {
            return {
                fill: info.color === 'gray' ? '#e2e8f0' : `var(--${info.color}-600, #4B5563)`,
                stroke: info.color === 'gray' ? '#6B7280' : `var(--${info.color}-400, #9CA3AF)`,
                text: info.color === 'gray' ? '#D1D5DB' : '#FFFFFF',
            };
        }
        return {
            fill: '#1F2937',
            stroke: '#e2e8f0',
            text: '#9CA3AF',
        };
    };

    // Color mapping for Tailwind colors to hex
    const colorMap = {
        gray: { fill: '#e2e8f0', stroke: '#6B7280', glow: '#6B7280' },
        blue: { fill: '#1E3A8A', stroke: '#3B82F6', glow: '#3B82F6' },
        yellow: { fill: '#78350F', stroke: '#F59E0B', glow: '#F59E0B' },
        green: { fill: '#14532D', stroke: '#22C55E', glow: '#22C55E' },
        emerald: { fill: '#064E3B', stroke: '#10B981', glow: '#10B981' },
        red: { fill: '#7F1D1D', stroke: '#EF4444', glow: '#EF4444' },
        cyan: { fill: '#164E63', stroke: '#06B6D4', glow: '#06B6D4' },
        purple: { fill: '#581C87', stroke: '#A855F7', glow: '#A855F7' },
    };

    const getStateColors = (state) => {
        const info = getStateInfo(state);
        const isCurrent = state === currentState;
        const colors = colorMap[info.color] || colorMap.gray;

        return {
            fill: isCurrent ? colors.fill : '#1F2937',
            stroke: isCurrent ? colors.stroke : '#e2e8f0',
            text: isCurrent ? '#FFFFFF' : '#9CA3AF',
            glow: isCurrent ? colors.glow : null,
        };
    };

    // Draw arrow between two nodes
    const drawArrow = (from, to, options = {}) => {
        const fromPos = NODE_POSITIONS[from];
        const toPos = NODE_POSITIONS[to];

        if (!fromPos || !toPos) return null;

        const halfW = NODE_WIDTH / 2;
        const halfH = NODE_HEIGHT / 2;
        const arrowPadding = 4; // Gap between arrow and node edge

        let startX, startY, endX, endY;
        let controlPoints = null;

        // Determine connection direction
        const dx = toPos.x - fromPos.x;
        const dy = toPos.y - fromPos.y;
        const isHorizontal = Math.abs(dx) > Math.abs(dy);
        const isGoingRight = dx > 0;
        const isGoingDown = dy > 0;

        if (isHorizontal) {
            // Horizontal connection: right edge to left edge
            if (isGoingRight) {
                startX = fromPos.x + halfW + arrowPadding;
                endX = toPos.x - halfW - arrowPadding;
            } else {
                startX = fromPos.x - halfW - arrowPadding;
                endX = toPos.x + halfW + arrowPadding;
            }
            startY = fromPos.y;
            endY = toPos.y;

            // Handle diagonal horizontal connections with a curve
            if (Math.abs(dy) > 10) {
                const midX = (startX + endX) / 2;
                controlPoints = { cx1: midX, cy1: startY, cx2: midX, cy2: endY };
            }
        } else {
            // Vertical connection: bottom edge to top edge
            startX = fromPos.x;
            endX = toPos.x;
            if (isGoingDown) {
                startY = fromPos.y + halfH + arrowPadding;
                endY = toPos.y - halfH - arrowPadding;
            } else {
                startY = fromPos.y - halfH - arrowPadding;
                endY = toPos.y + halfH + arrowPadding;
            }

            // Handle diagonal vertical connections with a curve
            if (Math.abs(dx) > 10) {
                const midY = (startY + endY) / 2;
                controlPoints = { cx1: startX, cy1: midY, cx2: endX, cy2: midY };
            }
        }

        // Special handling for CRAWLED -> ENRICHING (curved upward)
        if (from === 'CRAWLED' && to === 'ENRICHING') {
            startX = fromPos.x + halfW * 0.3;
            startY = fromPos.y - halfH - arrowPadding;
            endX = toPos.x - halfW - arrowPadding;
            endY = toPos.y;
            controlPoints = { cx1: startX, cy1: startY - 25, cx2: endX - 30, cy2: endY };
        }

        // Special handling for ENRICHED -> PROCESSING (curved downward)
        if (from === 'ENRICHED' && to === 'PROCESSING') {
            startX = fromPos.x;
            startY = fromPos.y + halfH + arrowPadding;
            endX = toPos.x;
            endY = toPos.y - halfH - arrowPadding;
            controlPoints = { cx1: startX, cy1: startY + 20, cx2: endX, cy2: endY - 20 };
        }

        // Special handling for CRAWLED -> PROCESSING (skip enrichment path)
        if (from === 'CRAWLED' && to === 'PROCESSING') {
            startX = fromPos.x + halfW + arrowPadding;
            startY = fromPos.y;
            endX = toPos.x - halfW - arrowPadding;
            endY = toPos.y;
        }

        // Special handling for CRAWLED -> ASSESSING (direct assessment, curve below)
        if (from === 'CRAWLED' && to === 'ASSESSING') {
            startX = fromPos.x + halfW + arrowPadding;
            startY = fromPos.y + halfH * 0.5;
            endX = toPos.x - halfW - arrowPadding;
            endY = toPos.y + halfH * 0.5;
            const midY = Math.max(startY, endY) + 35;
            controlPoints = { cx1: startX + 50, cy1: midY, cx2: endX - 50, cy2: midY };
        }

        // Special handling for ENRICHED -> ASSESSING (direct assessment, curve below main flow)
        if (from === 'ENRICHED' && to === 'ASSESSING') {
            startX = fromPos.x + halfW + arrowPadding;
            startY = fromPos.y + halfH * 0.5;
            endX = toPos.x - halfW - arrowPadding;
            endY = toPos.y - halfH * 0.3;
            const midY = 100 + 30;
            controlPoints = { cx1: startX + 40, cy1: midY, cx2: endX - 40, cy2: midY };
        }

        // Special handling for ERROR -> DISCOVERED (retry path, curve below)
        if (from === 'ERROR' && to === 'DISCOVERED') {
            startX = fromPos.x - halfW - arrowPadding;
            startY = fromPos.y;
            endX = toPos.x;
            endY = toPos.y + halfH + arrowPadding;
            const midY = fromPos.y + 20;
            controlPoints = { cx1: startX - 80, cy1: midY, cx2: endX - 80, cy2: midY };
        }

        // Special handling for various state -> ERROR transitions (curve down)
        if (to === 'ERROR' && from !== 'ERROR') {
            startX = fromPos.x;
            startY = fromPos.y + halfH + arrowPadding;
            endX = toPos.x;
            endY = toPos.y - halfH - arrowPadding;
            if (Math.abs(fromPos.x - toPos.x) > 50) {
                const midX = (startX + endX) / 2;
                controlPoints = { cx1: midX, cy1: startY + 20, cx2: midX, cy2: endY - 20 };
            }
        }

        // Special handling for ASSESSED -> various (redo paths)
        if (from === 'ASSESSED' && options.isRedo) {
            startX = fromPos.x - halfW - arrowPadding;
            startY = fromPos.y - halfH * 0.3;
            endX = toPos.x + halfW + arrowPadding;
            endY = toPos.y - halfH * 0.3;
            const midY = 100 - 50;
            controlPoints = { cx1: startX - 30, cy1: midY, cx2: endX + 30, cy2: midY };
        }

        // Build path
        let path;
        if (controlPoints) {
            path = `M ${startX} ${startY} C ${controlPoints.cx1} ${controlPoints.cy1}, ${controlPoints.cx2} ${controlPoints.cy2}, ${endX} ${endY}`;
        } else {
            path = `M ${startX} ${startY} L ${endX} ${endY}`;
        }

        const isActive = from === currentState || (options.isRetry && currentState === 'ERROR' && to === from);

        return (
            <g key={`${from}-${to}`}>
                <defs>
                    <marker
                        id={`arrowhead-${from}-${to}`}
                        markerWidth="8"
                        markerHeight="6"
                        refX="7"
                        refY="3"
                        orient="auto"
                    >
                        <polygon
                            points="0 0, 8 3, 0 6"
                            fill={isActive ? '#22C55E' : (options.isRetry ? '#EF4444' : '#6B7280')}
                        />
                    </marker>
                </defs>
                <path
                    d={path}
                    fill="none"
                    stroke={isActive ? '#22C55E' : (options.isRetry ? '#EF4444' : '#6B7280')}
                    strokeWidth={isActive ? 2.5 : 1.5}
                    strokeDasharray={options.optional || options.isRetry || options.isRedo ? '5,3' : 'none'}
                    markerEnd={`url(#arrowhead-${from}-${to})`}
                    opacity={options.isRedo ? 0.4 : 1}
                />
            </g>
        );
    };

    const handleNodeHover = (state) => {
        setHoveredState(state);
    };

    const handleNodeLeave = () => {
        setHoveredState(null);
    };

    return (
        <div className="relative">
            {/* SVG Diagram */}
            <svg
                viewBox="0 0 1080 250"
                className="w-full h-auto"
                style={{ minHeight: '220px' }}
            >
                {/* Background */}
                <rect width="1080" height="250" fill="#ffffff" rx="8" />

                {/* Transitions */}
                <g className="transitions">
                    {STATE_TRANSITIONS.map(({ from, to, optional, isRetry, isRedo }) =>
                        drawArrow(from, to, {
                            optional,
                            isRetry,
                            isRedo,
                            curved: from === 'CRAWLED' && to === 'ENRICHING',
                        })
                    )}
                </g>

                {/* Nodes */}
                <g className="nodes">
                    {Object.entries(NODE_POSITIONS).map(([state, pos]) => {
                        const info = getStateInfo(state);
                        const colors = getStateColors(state);
                        const isCurrent = state === currentState;
                        const isInProgress = isCurrent && isProcessingState(state);
                        const canClick = onNodeClick && getAvailableActions(currentState).length > 0;
                        return (
                            <g
                                key={state}
                                transform={`translate(${pos.x - NODE_WIDTH / 2}, ${pos.y - NODE_HEIGHT / 2})`}
                                className={`cursor-pointer transition-transform ${canClick ? 'hover:scale-105' : ''}`}
                                onClick={() => canClick && onNodeClick(state)}
                                onMouseEnter={(e) => handleNodeHover(state, e)}
                                onMouseLeave={handleNodeLeave}
                            >
                                {/* Glow effect for current state */}
                                {isCurrent && colors.glow && (
                                    <rect
                                        x="-4"
                                        y="-4"
                                        width={NODE_WIDTH + 8}
                                        height={NODE_HEIGHT + 8}
                                        rx="10"
                                        fill="none"
                                        stroke={colors.glow}
                                        strokeWidth="2"
                                        opacity="0.3"
                                        className={isInProgress ? 'animate-pulse' : ''}
                                    />
                                )}

                                {/* Node background */}
                                <rect
                                    width={NODE_WIDTH}
                                    height={NODE_HEIGHT}
                                    rx="6"
                                    fill={colors.fill}
                                    stroke={colors.stroke}
                                    strokeWidth={isCurrent ? 2 : 1}
                                />

                                {/* State label */}
                                <text
                                    x={NODE_WIDTH / 2}
                                    y={NODE_HEIGHT / 2 + 5}
                                    textAnchor="middle"
                                    fill={colors.text}
                                    fontSize="11"
                                    fontWeight={isCurrent ? 'bold' : 'normal'}
                                    fontFamily="system-ui, sans-serif"
                                >
                                    {info.label}
                                </text>

                                {/* Current indicator dot */}
                                {isCurrent && (
                                    <circle
                                        cx={NODE_WIDTH - 8}
                                        cy="8"
                                        r="4"
                                        fill={colors.stroke}
                                        className={isInProgress ? 'animate-ping' : ''}
                                    />
                                )}
                            </g>
                        );
                    })}
                </g>

                {/* Labels for branches */}
                <text x="570" y="18" fill="#9CA3AF" fontSize="10" fontStyle="italic">
                    (optional deep crawl)
                </text>
                <text x="430" y="235" fill="#9CA3AF" fontSize="10" fontStyle="italic">
                    (retry from error)
                </text>
            </svg>

            {/* Tooltip */}
            {hoveredState && (
                <div
                    className="absolute z-50 px-3 py-2 bg-gray-800 border border-gray-700 shadow-lg pointer-events-none"
                    style={{
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bottom: 'calc(100% + 10px)',
                    }}
                >
                    <div className="text-sm font-medium text-white">
                        {getStateInfo(hoveredState).label}
                    </div>
                    <div className="text-xs text-gray-400">
                        {getStateInfo(hoveredState).description}
                    </div>
                </div>
            )}

            {/* Legend */}
            {showLegend && (
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-400">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-0.5 bg-gray-500" />
                        <span>Normal transition</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-0.5 bg-gray-500" style={{ strokeDasharray: '2,2' }}>
                            <svg width="12" height="2">
                                <line x1="0" y1="1" x2="12" y2="1" stroke="#6B7280" strokeDasharray="2,2" />
                            </svg>
                        </div>
                        <span>Optional/Retry</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-50 border border-green-500" />
                        <span>Current state</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse" />
                        <span>Processing</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcessingStateFlow;
